import { useEffect, useRef, useState } from "react";
import axios from "axios";
import Loading from "../component1/widgets/Loading";
import "./FormSubmissionPreloader.css";

const SUBMISSION_METHODS = new Set(["post", "put", "patch", "delete"]);
const TRACKING_KEY = "__formSubmissionPreloaderTracked";

const isSubmissionRequest = (config) => {
  const method = (config?.method || "get").toLowerCase();
  return SUBMISSION_METHODS.has(method) && !config?.skipFormPreloader;
};

export default function FormSubmissionPreloader() {
  const [visible, setVisible] = useState(false);
  const [hasExternalLoader, setHasExternalLoader] = useState(false);
  const pendingRequestsRef = useRef(0);

  useEffect(() => {
    const handleRequestStart = (config) => {
      if (isSubmissionRequest(config)) {
        config[TRACKING_KEY] = true;
        pendingRequestsRef.current += 1;
        setVisible(true);
      }
      return config;
    };

    const handleRequestEnd = (config) => {
      if (!config?.[TRACKING_KEY]) {
        return;
      }

      pendingRequestsRef.current = Math.max(0, pendingRequestsRef.current - 1);
      if (pendingRequestsRef.current === 0) {
        setVisible(false);
      }
    };

    const requestInterceptor = axios.interceptors.request.use(
      handleRequestStart,
      (error) => {
        handleRequestEnd(error?.config);
        return Promise.reject(error);
      }
    );

    const responseInterceptor = axios.interceptors.response.use(
      (response) => {
        handleRequestEnd(response?.config);
        return response;
      },
      (error) => {
        handleRequestEnd(error?.config);
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  useEffect(() => {
    if (!visible || typeof document === "undefined") {
      setHasExternalLoader(false);
      return undefined;
    }

    const checkExternalLoader = () => {
      const found = Array.from(
        document.querySelectorAll(".custom-lottie-loader")
      ).some((el) => !el.closest(".form-submission-preloader-overlay"));
      setHasExternalLoader(found);
    };

    checkExternalLoader();

    const observer = new MutationObserver(checkExternalLoader);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
    };
  }, [visible]);

  if (!visible || hasExternalLoader) {
    return null;
  }

  return (
    <div className="form-submission-preloader-overlay">
      <div className="form-submission-preloader-box">
        <Loading width="120px" height="120px" />
      </div>
    </div>
  );
}
